import { ProgressColor } from '@/types';

export type ProgressMode = 'percentage' | 'label';

interface SemiCircleProgressBarProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  backgroundColor?: string;
  color?: ProgressColor;
  showLabel?: boolean;
  mode?: ProgressMode;
  labelText?: string;
}

const colorVarMap: Record<ProgressColor, string> = {
  low: 'var(--color-success)',
  moderate: 'var(--color-warning)',
  elevated: 'var(--color-warning-dark)',
  significant: 'var(--color-error-light)',
  high: 'var(--color-error-medium)',
  veryHigh: 'var(--color-error)',
  extreme: 'var(--color-error-dark)',
  primary: 'var(--color-primary)',
};

export const SemiCircleProgressBar: React.FC<SemiCircleProgressBarProps> = ({
  progress,
  size = 200,
  strokeWidth = 10,
  backgroundColor = 'var(--color-background)',
  color = 'primary',
  showLabel = false,
  mode = 'percentage',
  labelText = '',
}) => {
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  const progressColor = colorVarMap[color];

  return (
    <svg width={size} height={size / 2} viewBox={`0 0 ${size} ${size / 2}`}>
      <path
        d={describeArc(centerX, centerY, radius, 180, 0)}
        fill="none"
        stroke={backgroundColor}
        strokeWidth={strokeWidth}
      />

      <path
        d={describeArc(
          centerX,
          centerY,
          radius,
          180,
          180 - 180 * (progress / 100),
        )}
        fill="none"
        stroke={progressColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      {showLabel && (
        <text
          x="50%"
          y={size / 2 - strokeWidth * 1.2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={mode === 'percentage' ? size * 0.12 : size * 0.09}
          fill="var(--color-default)"
          fontWeight="bold"
        >
          {mode === 'percentage'
            ? progress
              ? `${Math.round(progress)}%`
              : 'N/A'
            : labelText}
        </text>
      )}
    </svg>
  );
};

function describeArc(
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(' ');
}

function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY - radius * Math.sin(angleInRadians),
  };
}
